import { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from './AuthContext';
import { useNotifications } from './NotificationsContext';

const SyncStatusContext = createContext(null);

// Ordered list of sources the indicator cycles through. The first four are
// host data; the last five are real delivery/conferencing channel statuses.
export const SYNC_STEPS = [
  'events', 'contacts', 'notifications', 'calendars',
  'telegram', 'email', 'max', 'yandex_telemost', 'yandex_mail',
];

const CHANNEL_STEPS = ['telegram', 'email', 'max', 'yandex_telemost', 'yandex_mail'];
const CACHE_TTL = 30000; // re-use a fresh /sync/status within 30s of navigation
const STEP_DELAY = 150; // ms between revealing each source (the "dynamic" cascade)
const HOLD_COMPLETE = 1500; // ms the "all synced" state lingers before fading

export function SyncStatusProvider({ children }) {
  const { user } = useAuth();
  const { action_total: actionTotal } = useNotifications();
  const location = useLocation();
  const [phase, setPhase] = useState('idle'); // idle | syncing | complete
  const [done, setDone] = useState([]); // step ids revealed so far
  const [data, setData] = useState(null); // { counts, channels }
  const runIdRef = useRef(0);
  const cacheRef = useRef({ ts: 0, data: null });

  const fetchStatus = useCallback(async () => {
    const now = Date.now();
    if (cacheRef.current.data && now - cacheRef.current.ts < CACHE_TTL) {
      return cacheRef.current.data;
    }
    try {
      const { data: res } = await api.get('/sync/status');
      cacheRef.current = { ts: now, data: res };
      return res;
    } catch {
      return cacheRef.current.data || { counts: {}, channels: {} };
    }
  }, []);

  const run = useCallback(async () => {
    if (!user) return;
    const id = ++runIdRef.current;
    setPhase('syncing');
    setDone([]);
    const statusPromise = fetchStatus();
    for (let i = 0; i < SYNC_STEPS.length; i += 1) {
      await new Promise((r) => setTimeout(r, STEP_DELAY));
      if (runIdRef.current !== id) return; // a newer run superseded this one
      const res = await statusPromise;
      if (runIdRef.current !== id) return;
      setData(res);
      setDone((prev) => [...prev, SYNC_STEPS[i]]);
    }
    if (runIdRef.current !== id) return;
    setPhase('complete');
    await new Promise((r) => setTimeout(r, HOLD_COMPLETE));
    if (runIdRef.current !== id) return;
    setPhase('idle');
  }, [user, fetchStatus]);

  useEffect(() => {
    if (!user) { setPhase('idle'); setDone([]); return; }
    run();
    // re-run on login AND on every in-app navigation (per product decision)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.pathname]);

  const pct = Math.round((done.length / SYNC_STEPS.length) * 100);
  const currentStep = phase === 'syncing'
    ? SYNC_STEPS[Math.min(done.length, SYNC_STEPS.length - 1)]
    : null;

  const stepStatus = useCallback((step) => {
    if (CHANNEL_STEPS.includes(step)) {
      return (data?.channels || {})[step] || 'off';
    }
    return 'connected';
  }, [data]);

  return (
    <SyncStatusContext.Provider value={{
      phase, done, pct, currentStep, data,
      notifCount: actionTotal || 0, stepStatus, rerun: run,
    }}>
      {children}
    </SyncStatusContext.Provider>
  );
}

export function useSyncStatus() {
  return useContext(SyncStatusContext) || {
    phase: 'idle', done: [], pct: 0, currentStep: null,
    data: null, notifCount: 0, stepStatus: () => 'off', rerun: () => {},
  };
}
